/**
 * Background Client - Chrome Extension Message Proxy
 *
 * This module creates a typed ApiClient that proxies all method calls
 * to the Chrome extension background script via chrome.runtime.sendMessage.
 *
 * Used when builders are injected into pages by the extension (non-standalone mode).
 * The extension's background script handles these messages in api-request.js
 * and calls the actual API endpoints.
 */

import type { ApiClient } from './types';

declare const chrome: {
  runtime: {
    sendMessage: (
      message: { action: string; value: { method: string; args: unknown[] } },
      callback: (response: [unknown, boolean?]) => void
    ) => void;
  };
};

/**
 * All methods from the ApiClient interface.
 * This array must include every method from ApiClient.
 *
 * The TypeScript assertion below will cause a compile error if any methods are missing.
 */
export const API_CLIENT_METHODS = [
  // Gates & Account
  'getAccountGates',
  'getUserInfo',

  // User Management
  'getUser',
  'updateUser',
  'getUserAccounts',
  'getUserAccountsV2',
  'despoof',
  'updateUsageProperties',
  'getUserPropertiesLabels',

  // Flow Operations
  'getFlowsSummary',
  'getFlow',
  'getLaunchpadEligibleFlows',
  'createFlow',
  'replaceFlow',
  'updateFlow',
  'createStepGroup',
  'updateStepGroup',
  'updateStepChild',
  'moveStepGroup',
  'createStepChild',
  'moveStepChild',
  'deleteStepGroup',
  'deleteStepChild',
  'getLayouts',

  // Experience Operations
  'getExperiencesSummary',
  'getExperience',
  'createExperience',
  'updateExperience',
  'createStep',
  'updateStep',
  'removeStep',
  'moveStep',

  // Locale Management
  'getLocales',
  'createLocale',
  'removeLocale',
  'updateLocale',
  'autoTranslate',

  // Custom Events
  'getCustomEvents',
  'createCustomEvent',
  'updateCustomEvent',

  // Templates
  'getTemplates',
  'createTemplate',
  'updateTemplate',
  'deleteTemplate',

  // Images
  'fetchImages',
  'uploadImage',

  // Rules
  'getRule',
  'updateRule',

  // Surveys
  'getSurveyTypes',
  'createSurveyEntity',
  'createSurveyField',

  // Campaigns & Tactics (Agent Studio)
  'getCampaigns',
  'addTacticContent',

  // Tags, Themes, Entitlements, Doc Sites
  'getTags',
  'getThemes',
  'getEntitlements',
  'getDocSiteIntegrations',

  // Dashboard API
  'getFlowPreview',
  'getUserProperties',
  'trackMetric',

  // Analytics API
  'getEventCounts',
  'getClickstreamEventCount',
  'triggerClickstreamBackfill',

  // Mobile-Specific Methods
  'getAccount',
  'getApps',
  'getScreen',
  'getScreensSummary',
  'getSDKToken',
  'updateScreen',
  'removeScreen',
  'addStep',
  'generateSmartText',
  'getLambdaTemplateImage',

  // Experience Themes (Mobile)
  'getExperienceThemes',
  'createExperienceTheme',
  'publishExperienceTheme',
  'getExperienceTheme',
  'updateExperienceTheme',
  'getCompiledExperienceTheme',

  // Content Templates (Mobile)
  'getContentTemplates',
  'createContentTemplate',
  'updateContentTemplate',
  'deleteContentTemplate',

  // Custom Snippets
  'getCustomSnippets',
  'getCustomSnippet',
  'createCustomSnippet',
  'updateCustomSnippet',
  'publishCustomSnippet',
  'deleteCustomSnippet',
] as const;

// Type-level assertion to ensure all ApiClient methods are in the array
// If a method is added to ApiClient but not to API_CLIENT_METHODS, this will cause a compile error
type MethodsInArray = (typeof API_CLIENT_METHODS)[number];
type MissingMethods = Exclude<keyof ApiClient, MethodsInArray>;
type AssertNoMissingMethods = MissingMethods extends never
  ? true
  : { error: 'Missing methods in API_CLIENT_METHODS'; missing: MissingMethods };
export const assertAllMethodsPresent: AssertNoMissingMethods = true;

/**
 * Creates a proxy function that sends API requests to the Chrome extension background
 *
 * @param method - The API method name to call
 * @returns A function that proxies the call via Chrome messaging
 */
const createProxyMethod = <T>(method: string) => {
  return (...args: unknown[]): Promise<T> =>
    new Promise((resolve, reject) => {
      chrome.runtime.sendMessage(
        { action: 'appcues:api-request', value: { method, args } },
        (response: [unknown, boolean?]) => {
          const [result, error] = response;
          if (error) {
            reject(result);
          } else {
            resolve(result as T);
          }
        }
      );
    });
};

/**
 * Creates a background client that proxies all ApiClient methods
 * to the Chrome extension background script.
 *
 * This replaces the individual api-client.js files in each app that
 * manually listed specific methods. Now all methods from ApiClient
 * interface are automatically available.
 *
 * @returns A fully typed ApiClient that proxies calls to the extension
 */
export const createBackgroundClient = (): ApiClient => {
  // Build the client by creating proxy methods for each API method
  const client = API_CLIENT_METHODS.reduce<Record<string, unknown>>(
    (acc, method) => {
      acc[method] = createProxyMethod(method);
      return acc;
    },
    {}
  );

  return client as unknown as ApiClient;
};

export default createBackgroundClient;
